import { styled } from '@mui/material/styles';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { VyThemeProvider } from '../../../theme-provider';

/**
 * Root for a page that uses the design system. Wraps children in
 * `VyThemeProvider` so every Vy primitive inside reads the correct theme,
 * and paints `theme.palette.background.default` across the whole region
 * with `minHeight: 100%` — so the page background is consistent with the
 * theme tokens and doesn't leak the host shell's bg through.
 *
 * Use this as the **outermost** element of any new page that follows the
 * recipe in `PAGES.md`. When the page also needs the legacy `apps/app`
 * sidebar/topbar chrome (the standard for in-app pages), wrap `VyPage`
 * inside the host's layout component:
 *
 * ```tsx
 * <VyLayout>          // legacy app chrome (sidebar, top bar)
 *   <VyPage>          // design-system theme + bg
 *     {pageContent}
 *   </VyPage>
 * </VyLayout>
 * ```
 *
 * For a standalone page that should not have the legacy chrome, drop
 * `VyLayout` and use `<VyPage>` directly under the route.
 */
export interface VyPageProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

const PageRoot = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100%',
  width: '100%',
  // Ensure flex children (page header + content) stack correctly.
  display: 'flex',
  flexDirection: 'column',
}));

export const VyPage = forwardRef<HTMLDivElement, VyPageProps>(
  function VyPage({ children, ...rest }, ref) {
    return (
      <VyThemeProvider>
        <PageRoot ref={ref} {...rest}>
          {children}
        </PageRoot>
      </VyThemeProvider>
    );
  },
);
